import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useAction } from 'wasp/client/operations';
import { getSectionById, deleteSection, getSections } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import {
  Layers,
  Edit3,
  Trash2,
  CheckCircle,
  XCircle,
  GitBranch,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import {
  IndividualViewPageLayout,
  SidebarSection,
  ActionButtons,
  DetailField,
  ActionButtonLink,
  ActionButtonDanger
} from '../../components/layout';
import { toast } from '../../hooks/use-toast';

export default function ViewSectionPage() {
  const { sectionId } = useParams<{ sectionId: string }>();
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const deleteSectionFn = useAction(deleteSection);

  // Fetch section data
  const {
    data: section,
    isLoading,
    error
  } = useQuery(getSectionById, { id: sectionId! });

  // Fetch all sections for navigation
  const { data: allSections = [] } = useQuery(getSections);

  const handleBack = () => {
    navigate('/admin/sections');
  };

  // Find current index and get prev/next sections
  const currentIndex = (allSections as any[]).findIndex((sec: any) => sec.id === sectionId);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < (allSections as any[]).length - 1;
  const prevSection = hasPrev ? (allSections as any[])[currentIndex - 1] : null;
  const nextSection = hasNext ? (allSections as any[])[currentIndex + 1] : null;

  const handleDelete = async () => {
    if (!section) return;
    if (!confirm(`Are you sure you want to delete "${section.section_key}"?`)) return;

    try {
      await deleteSectionFn({ id: section.id });
      toast({
        title: "Success",
        description: "Section deleted successfully"
      });
      navigate('/admin/sections');
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to delete section",
        variant: "destructive"
      });
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600">You must be logged in to view sections.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading section...</p>
        </div>
      </div>
    );
  }

  if (error || !section) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Layers className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Section Not Found</h3>
          <p className="text-gray-600 mb-4">The section you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Sections
          </button>
        </div>
      </div>
    );
  }

  // Get section type label
  const getSectionType = () => {
    if (section.isBase) return 'Base';
    if (section.isTypeSpecific) return 'Type-Specific';
    return 'Standard';
  };

  const sidebarContent = (
    <>
      <SidebarSection isFirst>
        <div className="space-y-3">
          {/* Navigation Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => prevSection && navigate(`/admin/sections/${prevSection.id}`)}
              disabled={!hasPrev}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
              title={hasPrev ? `Previous: ${prevSection?.section_key}` : 'No previous item'}
            >
              <ChevronUp className="w-4 h-4" />
              <span className="text-sm font-medium">Up</span>
            </button>
            <button
              onClick={() => nextSection && navigate(`/admin/sections/${nextSection.id}`)}
              disabled={!hasNext}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
              title={hasNext ? `Next: ${nextSection?.section_key}` : 'No next item'}
            >
              <span className="text-sm font-medium">Down</span>
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>

          {/* Action Buttons */}
          <ActionButtons>
            <ActionButtonLink
              to={`/admin/sections/${section.id}/edit`}
            >
              Edit
            </ActionButtonLink>
            <ActionButtonDanger
              onClick={handleDelete}
            >
              Delete
            </ActionButtonDanger>
          </ActionButtons>
        </div>
      </SidebarSection>

      <SidebarSection title="Details">
        <div className="space-y-4">
          <DetailField label="Section Key" value={section.section_key} />
          <DetailField label="Type" value={getSectionType()} />
          <DetailField label="Order" value={section.order} />
          <DetailField label="Status" value={section.archived ? 'Archived' : 'Active'} />
          <DetailField label="Sub-Sections" value={section._count?.subSections || 0} />
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualViewPageLayout
      breadcrumbItems={[
        { label: 'Admin', href: '/admin' },
        { label: 'Sections', href: '/admin/sections', isClickable: true }
      ]}
      currentPageUrl={typeof window !== 'undefined' ? window.location.href : ''}
      sidebarContent={sidebarContent}
      layoutVariant="taxonomy"
      authUser={user}
    >
      {/* Section Header */}
      <div className="mb-8">
        {/* Title and Status */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900 font-mono">{section.section_key}</h1>
            </div>

            {section.parentSection && (
              <p className="text-lg text-gray-600 mb-4">
                Parent: <Link to={`/admin/sections/${section.parentSection.id}`} className="text-blue-600 hover:text-blue-800">{section.parentSection.section_key}</Link>
              </p>
            )}
          </div>

          {/* Status */}
          <div className="flex items-center gap-2 ml-4">
            {section.archived ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-red-100 text-red-800">
                <XCircle className="w-4 h-4" />
                <span className="font-medium">Archived</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-green-100 text-green-800">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">Active</span>
              </div>
            )}
          </div>
        </div>

        {/* Meta Information */}
        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 border-b border-gray-200 pb-6">
          <div className="flex items-center gap-2">
            <span>Order: {section.order}</span>
          </div>

          <div className="flex items-center gap-2">
            <span>Type: {getSectionType()}</span>
          </div>
        </div>
      </div>

      {/* Section Content */}
      <div className="space-y-6">
        {/* Type Badges */}
        <div className="flex flex-wrap gap-3 mb-6">
          <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-blue-50 border-blue-200 text-blue-700">
            <Layers className="w-5 h-5" />
            <span className="font-medium">{getSectionType()}</span>
          </span>

          {section.isBase && (
            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-purple-50 border-purple-200 text-purple-700">
              <span className="font-medium">Base Section</span>
            </span>
          )}

          {section.isTypeSpecific && (
            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-orange-50 border-orange-200 text-orange-700">
              <span className="font-medium">Type-Specific</span>
            </span>
          )}
        </div>

        {/* Quick Info */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Info</h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <Layers className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-gray-600">Section Key</p>
                <p className="font-medium text-gray-900 font-mono">{section.section_key}</p>
              </div>
            </div>

            {section.parentSection && (
              <div className="flex items-start gap-3">
                <GitBranch className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-600">Parent Section</p>
                  <Link
                    to={`/admin/sections/${section.parentSection.id}`}
                    className="font-medium text-blue-600 hover:text-blue-800"
                  >
                    {section.parentSection.section_key}
                  </Link>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <Layers className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-gray-600">Sub-Sections</p>
                <p className="font-medium text-gray-900">{section._count?.subSections || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sub-Sections List */}
        {section.subSections && section.subSections.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <GitBranch className="w-5 h-5" />
              Sub-Sections
            </h3>
            <div className="space-y-2">
              {section.subSections.map((subSection: any) => (
                <Link
                  key={subSection.id}
                  to={`/admin/sections/${subSection.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Layers className="w-4 h-4 text-gray-400" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 font-mono">{subSection.section_key}</p>
                  </div>
                  {subSection.archived ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">Archived</span>
                  ) : (
                    <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">Active</span>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Translations */}
        {section.translations && section.translations.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Translations</h3>
            <div className="space-y-3">
              {section.translations.map((translation: any) => (
                <div
                  key={translation.id}
                  className="p-3 rounded-lg bg-gray-50"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-200 text-gray-700 uppercase">
                      {translation.language_code}
                    </span>
                    <span className="font-medium text-gray-900">{translation.label}</span>
                  </div>
                  {translation.description && (
                    <p className="text-sm text-gray-600">{translation.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Info */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Info</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Status</span>
              <span className="font-medium text-gray-900">{section.archived ? 'Archived' : 'Active'}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Display Order</span>
              <span className="font-medium text-gray-900">{section.order}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Is Base</span>
              <span className="font-medium text-gray-900">{section.isBase ? 'Yes' : 'No'}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Is Type-Specific</span>
              <span className="font-medium text-gray-900">{section.isTypeSpecific ? 'Yes' : 'No'}</span>
            </div>
          </div>
        </div>
      </div>
    </IndividualViewPageLayout>
  );
}



